import React, { useState, useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import AllotmentResultsTable from "../AllotmentResultsTable";

const AllotmentForm = ({
  shareAllotments,
  mode, // 'rightsIssue' | 'proRata'
  submittedAllocations, // parent-controlled array
  onUpdateAllocations, // (nextAllocations) => void
  onSubmit, // async fn(payload, { setSubmitting }) => resultAllocationRow
  onCancel, // () => void
  onCompleteAllProcessed, // () => void (called when all categories processed)
  companyId = null,
  // Legacy props for backward compatibility
  entityType,
  selectedOption,
  setSelectedOption,
  onBack,
  processedShares: parentProcessedShares,
  onUpdateProcessedShares,
  submittedAllocations: parentSubmittedAllocations,
  onUpdateSubmittedAllocations,
}) => {
  // Safe context usage with error handling
  const contextValue = useContext(UpdateCompanyDetailsContext) || {};
  const { transactions, fetchTransactions } = contextValue;

  // Track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  // Local UI state
  const [processedShares, setProcessedShares] = useState(new Set());
  const [showTargetsDropdown, setShowTargetsDropdown] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState(null);

  // Get nominal shares from Redux (this is a hook, so must be called before any early returns)
  const nominalShares = useSelector(
    (state) => state.companyDashboardSlice?.company?.nominalShares || []
  );

  // ===== ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS =====

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Close dropdown to prevent any potential memory leaks
      setShowTargetsDropdown(false);
    };
  }, []);

  // Get normalized share allotments from transactions or props
  const normalizedShareAllotments = (() => {
    try {
      // Prioritize shareAllotments prop if it's provided and not empty
      if (Array.isArray(shareAllotments) && shareAllotments.length > 0) {
        return shareAllotments.filter(
          (change) => change && change.classOfShares
        );
      }

      // Fallback to shareCapitalChanges from transactions ONLY
      if (
        transactions &&
        transactions.length > 0 &&
        transactions[0]?.shareCapitalChanges
      ) {
        const shareCapitalChanges = transactions[0].shareCapitalChanges;

        // Filter out any null/undefined entries and return the array
        return Array.isArray(shareCapitalChanges)
          ? shareCapitalChanges.filter(
              (change) => change && change.classOfShares
            )
          : [];
      }
      return [];
    } catch (error) {
      console.error("Error getting share capital changes:", error);
      return [];
    }
  })();

  const filteredShareAllotments = (() => {
    try {
      // If no submitted allocations yet, return all shares
      if (!submittedAllocations || submittedAllocations.length === 0) {
        return normalizedShareAllotments;
      }

      // Create a Set of already-processed share keys for fast lookup
      const processedShareKeys = new Set(
        submittedAllocations
          .filter(
            (allocation) =>
              allocation &&
              allocation.classOfShares &&
              allocation.sharesOnOffer !== undefined
          )
          .map(
            (allocation) =>
              `${allocation.classOfShares}#${Number(allocation.sharesOnOffer)}`
          )
      );

      // Filter out shares that have already been processed
      const unprocessedShares = normalizedShareAllotments.filter((share) => {
        if (
          !share ||
          !share.classOfShares ||
          share.sharesOnOffer === undefined
        ) {
          return false;
        }
        const shareKey = `${share.classOfShares}#${Number(
          share.sharesOnOffer
        )}`;
        return !processedShareKeys.has(shareKey);
      });

      return unprocessedShares;
    } catch (error) {
      console.error("Error filtering share allotments:", error);
      return normalizedShareAllotments;
    }
  })();

  // Build stable key for a share allotment
  const getShareKey = (share) =>
    share ? `${share.classOfShares}#${Number(share.sharesOnOffer)}` : "";

  // Get the current share being processed
  const getCurrentShare = () => {
    try {
      // Don't return current share if we're editing an allocation
      if (editingAllocation) {
        return null;
      }

      if (!Array.isArray(filteredShareAllotments)) {
        return null;
      }

      // Simply return the first share - parent is already filtering
      const availableShares = filteredShareAllotments.filter((share) => {
        if (!share) return false;
        return true;
      });
      return availableShares[0] || null;
    } catch (error) {
      console.error("Error getting current share:", error);
      return null;
    }
  };

  const currentShare = getCurrentShare();

  // Refresh transactions on initial mount only
  useEffect(() => {
    const refreshOnMount = async () => {
      if (fetchTransactions && typeof fetchTransactions === "function") {
        await fetchTransactions();
      }
    };

    refreshOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // ===== NOW SAFE TO DO EARLY RETURNS - AFTER ALL HOOKS =====

  // Early safety check for required props (after all hooks are declared)
  if (!onSubmit || typeof onSubmit !== "function") {
    console.error(
      "AllotmentForm: onSubmit prop is required and must be a function"
    );
    return <div>Error: Invalid configuration</div>;
  }

  // Early return if no valid data to prevent errors
  if (
    !Array.isArray(filteredShareAllotments) ||
    filteredShareAllotments.length === 0
  ) {
    return null; // Return null instead of showing message - parent will handle UI
  }

  // If there's no current share to process and not editing, don't show the form
  if (!currentShare && !editingAllocation) {
    return null; // Return null - parent will show "Add another Class of Shares" or completion message
  }

  // ===== COMPONENT LOGIC CONTINUES =====

  // Use parent-controlled allocations or fallback to legacy state
  const actualSubmittedAllocations =
    submittedAllocations || parentSubmittedAllocations || [];
  const actualOnUpdateAllocations =
    onUpdateAllocations || onUpdateSubmittedAllocations;

  // Derive mode from props or selectedOption for backward compatibility
  const actualMode =
    mode || (selectedOption === "rightsIssue" ? "rightsIssue" : "proRata");

  const totalShares = filteredShareAllotments.length;
  const remainingShares = totalShares - processedShares.size;

  // Helper functions for managing processed shares
  const markCurrentShareAsProcessed = () => {
    const key = getShareKey(currentShare);
    if (key && isMountedRef.current) {
      setProcessedShares((prev) => new Set([...prev, key]));
    }
  };

  const handleDeleteAllocation = (index) => {
    try {
      const allocation = actualSubmittedAllocations[index];
      if (!allocation) {
        console.error(
          "Cannot delete allocation: allocation not found at index",
          index
        );
        return;
      }

      const updatedAllocations = actualSubmittedAllocations.filter(
        (_, i) => i !== index
      );

      if (actualOnUpdateAllocations) {
        actualOnUpdateAllocations(updatedAllocations);
      }

      // Remove from processed shares so it can be re-processed (only if mounted)
      if (
        isMountedRef.current &&
        allocation.classOfShares &&
        allocation.sharesOnOffer !== undefined
      ) {
        const key = `${allocation.classOfShares}#${Number(
          allocation.sharesOnOffer
        )}`;
        setProcessedShares((prev) => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });

        // Clear editing state
        setEditingAllocation(null);
      }
    } catch (error) {
      console.error("Error deleting allocation:", error);
    }
  };

  const handleUpdateAllocation = (index, updatedAllocation) => {
    const updatedAllocations = [...actualSubmittedAllocations];
    updatedAllocations[index] = updatedAllocation;

    if (actualOnUpdateAllocations) {
      actualOnUpdateAllocations(updatedAllocations);
    }
  };

  const handleFinalSubmit = () => {
    // Call parent's onSubmit with a special flag to indicate final submission
    onSubmit(
      {
        option: actualMode,
        isCompleteSubmission: true,
        allAllocations: actualSubmittedAllocations,
      },
      { setSubmitting: () => {} }
    );
  };

  // Determine initial values from current share
  const getInitialValues = () => {
    try {
      // If editing an allocation, pre-fill with existing data
      if (editingAllocation) {
        return {
          shareCategory: editingAllocation.classOfShares || "",
          sharesOnOffer: editingAllocation.sharesOnOffer || "",
          forEvery: editingAllocation.forEvery || "",
          allotAmount: editingAllocation.allotAmount || "",
          paidUpShares:
            editingAllocation.AmountCreditedAsPaidUpCapitalPerShare || "",
          distributionTargetClasses:
            editingAllocation.distributionTargetClasses || [],
        };
      }

      // When processing next share, always use currentShare data
      if (currentShare) {
        return {
          sharesOnOffer: currentShare.sharesOnOffer || "",
          shareCategory: currentShare.classOfShares || "",
          forEvery: "",
          allotAmount: "",
          paidUpShares: "",
          distributionTargetClasses: [],
        };
      }

      return {
        sharesOnOffer: "",
        shareCategory: "",
        forEvery: "",
        allotAmount: "",
        paidUpShares: "",
        distributionTargetClasses: [],
      };
    } catch (error) {
      console.error("Error getting initial values:", error);
      return {
        sharesOnOffer: "",
        shareCategory: "",
        forEvery: "",
        allotAmount: "",
        paidUpShares: "",
        distributionTargetClasses: [],
      };
    }
  };

  const initialValues = getInitialValues();

  const getOfferPricePerShare = () => {
    try {
      // Get from currentShare (which is now from shareCapitalChanges)
      if (currentShare?.offerPricePerShare) {
        return Number(currentShare.offerPricePerShare);
      }

      // If editing, get from editingAllocation
      if (editingAllocation?.offerPricePerShare) {
        return Number(editingAllocation.offerPricePerShare);
      }

      return null;
    } catch (error) {
      console.error("Error getting offer price per share:", error);
      return null;
    }
  };

  // Validation schema using Yup - conditional based on mode
  const validationSchema = Yup.object().shape({
    shareCategory: Yup.string().required("Share category is required"),
    sharesOnOffer: Yup.number()
      .integer("Must be a whole number")
      .min(1, "Must be at least 1")
      .required("Shares on offer is required"),
    // Validate that paid-up amount does not exceed offer price per share
    paidUpShares: Yup.number()
      .required("Amount credited as paid-up per share is required")
      .test("not-greater-than-offer-price", function (value) {
        const offerPrice = getOfferPricePerShare();
        if (offerPrice === null || offerPrice === undefined) {
          // If we can't determine offer price, allow the validation to pass
          // This prevents blocking the form when offer price data is unavailable
          return true;
        }

        if (Number(value) > offerPrice) {
          return this.createError({
            message: `Amount credited as paid-up per share (${value}) cannot be greater than offer price per share (${offerPrice})`,
          });
        }

        return true;
      }),
    // If selected share category has sharesNumber === 0 (or is missing), require at least one target class
    distributionTargetClasses: Yup.array().test(
      "require-when-nonexistent",
      "Select at least one target class to base distribution on",
      function (val) {
        const sc = this.parent.shareCategory;
        const n = nominalShares.find((ns) => ns?.sharesCategory === sc);
        const sharesNum = Number(n?.sharesNumber || 0);
        const nonExistent = !n || sharesNum === 0;
        if (!nonExistent) return true;
        return Array.isArray(val) && val.length >= 1;
      }
    ),
    // Rights Issue specific fields
    ...(actualMode === "rightsIssue" && {
      forEvery: Yup.number()
        .integer("Must be a whole number")
        .min(1, "Must be at least 1")
        .required("For every is required"),
      allotAmount: Yup.number()
        .min(0.01, "Must be greater than 0")
        .required("Allot amount is required"),
    }),
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Results table is now handled by parent component - removed from here */}

      <div className="flex flex-col gap-2 bg-gray-50 rounded-lg p-2">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-700">
            {actualMode === "rightsIssue" ? "Rights Issue" : "Pro Rata"}{" "}
            Allocation
          </h2>
        </div>

        {/* Removed remaining shares display - processing one allocation at a time */}

        <Formik
          key={`form-${currentShare?.classOfShares || "none"}-${
            currentShare?.sharesOnOffer || "none"
          }-processed-${processedShares.size}`}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              if (!isMountedRef.current) {
                return;
              }

              const payload = {
                option: actualMode,
                shareCategory: values.shareCategory,
                sharesOnOffer: values.sharesOnOffer,
                paidUpShares: values.paidUpShares,
                distributionTargetClasses:
                  values.distributionTargetClasses || [],
                ...(actualMode === "rightsIssue" && {
                  forEvery: values.forEvery,
                  allotAmount: values.allotAmount,
                }),
              };

              // Call the parent's onSubmit and handle the response
              const result = await onSubmit(payload, { setSubmitting });

              // Only proceed if component is still mounted
              if (!isMountedRef.current) {
                return;
              }

              // Note: State is now updated directly in the transaction functions
              // This prevents race conditions and ensures immediate UI response

              // Form submission completed successfully
              if (isMountedRef.current) {
                // Parent component will handle closing the form and showing results table
              }
            } catch (error) {
              console.error("❌ Error processing allocation:", error);
              // Optionally show user-friendly error message here
            } finally {
              // Only update state if component is still mounted
              if (isMountedRef.current) {
                setSubmitting(false);
              }
            }
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => {
            // Get the current offer price for display
            const currentOfferPrice = getOfferPricePerShare();

            return (
              <Form className="flex flex-col gap-4">
                <div className="w-full flex flex-col gap-4 mt-4 p-2 bg-white rounded shadow">
                  <div className="flex items-center">
                    <label className="w-1/4 text-gray-700">
                      Share Category
                    </label>
                    <div className="w-full">
                      <Field
                        type="text"
                        name="shareCategory"
                        placeholder="Enter share category"
                        className="w-full p-2 border border-gray-300 bg-gray-200 rounded"
                        disabled
                      />
                      <ErrorMessage
                        name="shareCategory"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/4 text-gray-700">
                      Shares on Offer
                    </label>
                    <div className="w-full">
                      <Field
                        type="number"
                        name="sharesOnOffer"
                        placeholder="Min 1 - Max of (Unassigned)"
                        className="w-full p-2 border border-gray-300 bg-gray-200 rounded"
                        disabled
                      />
                      <ErrorMessage
                        name="sharesOnOffer"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/4 text-gray-700">
                      Amount credited as paid-up per share
                    </label>
                    <div className="w-full">
                      <Field
                        type="number"
                        name="paidUpShares"
                        placeholder="Enter Amount..."
                        className="w-full p-2 border border-gray-300 rounded"
                      />
                      {currentOfferPrice !== null && (
                        <div className="text-xs text-gray-500 mt-1">
                          Maximum allowed: {currentOfferPrice} (Offer price per
                          share)
                        </div>
                      )}
                      <ErrorMessage
                        name="paidUpShares"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>

                  {/* Rights Issue specific fields */}
                  {actualMode === "rightsIssue" && (
                    <div className="flex items-center">
                      <label className="w-1/4 text-gray-700">For every</label>
                      <div className="w-full flex justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <Field
                              type="number"
                              name="forEvery"
                              placeholder="Eg. 1"
                              className="w-full p-2 border border-gray-300 rounded"
                            />
                            <ErrorMessage
                              name="forEvery"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>
                          <label className="flex w-auto text-gray-700 mr-2 text-center">
                            Allot
                          </label>
                        </div>
                        <div className="flex-1">
                          <Field
                            type="number"
                            name="allotAmount"
                            placeholder="Eg. 1000"
                            className="w-full p-2 border border-gray-300 rounded"
                          />
                          <ErrorMessage
                            name="allotAmount"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Conditional distribution target classes */}
                  {(() => {
                    const sel = nominalShares.find(
                      (n) => n?.sharesCategory === values.shareCategory
                    );
                    const nonExistent =
                      !sel || Number(sel?.sharesNumber || 0) === 0;
                    if (!nonExistent) return null;
                    return (
                      <div className="flex items-start">
                        <label className="w-1/4 text-gray-700 pt-2">
                          Distribute to holders of classes
                        </label>
                        <div className="w-full">
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => {
                                if (isMountedRef.current) {
                                  setShowTargetsDropdown((prev) => !prev);
                                }
                              }}
                              className="w-full flex justify-between items-center p-2 border border-gray-300 rounded"
                            >
                              <span className="text-gray-700 w-full">
                                {(values.distributionTargetClasses || [])
                                  .length > 0 ? (
                                  <span className="flex text-xs flex-wrap gap-1">
                                    {(
                                      values.distributionTargetClasses || []
                                    ).map((cls) => (
                                      <span
                                        key={cls}
                                        className="inline-flex items-start px-2 py-1 rounded bg-sflPrimary text-white text-xs"
                                      >
                                        {cls}
                                      </span>
                                    ))}
                                  </span>
                                ) : (
                                  "Select Distribution Class(es)"
                                )}
                              </span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 transform transition-transform ${
                                  showTargetsDropdown
                                    ? "rotate-180"
                                    : "rotate-0"
                                }`}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                            {showTargetsDropdown && (
                              <div className="absolute z-10 mt-1 w-1/2 bg-gray-150 border border-gray-200 rounded shadow max-h-60 overflow-auto">
                                <ul className="py-1 text-sm text-gray-700">
                                  {nominalShares
                                    .filter(
                                      (n) =>
                                        n?.sharesCategory !==
                                        values.shareCategory
                                    )
                                    .map((n) => {
                                      const value = n.sharesCategory;
                                      const checked = (
                                        values.distributionTargetClasses || []
                                      ).includes(value);
                                      return (
                                        <li
                                          key={n._id || value}
                                          className="px-2 py-1 hover:bg-gray-50"
                                        >
                                          <label className="inline-flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              className="h-4 w-4"
                                              checked={checked}
                                              onChange={(e) => {
                                                if (!isMountedRef.current)
                                                  return;

                                                const prev =
                                                  values.distributionTargetClasses ||
                                                  [];
                                                const next = e.target.checked
                                                  ? Array.from(
                                                      new Set([...prev, value])
                                                    )
                                                  : prev.filter(
                                                      (v) => v !== value
                                                    );
                                                setFieldValue(
                                                  "distributionTargetClasses",
                                                  next
                                                );
                                              }}
                                            />
                                            <span>{value}</span>
                                          </label>
                                        </li>
                                      );
                                    })}
                                </ul>
                              </div>
                            )}
                          </div>
                          <ErrorMessage
                            name="distributionTargetClasses"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="flex justify-end mt-4 gap-2">
                  <button
                    type="submit"
                    className="inline-flex items-center bg-sflPrimary text-white py-2 px-4 rounded"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <SpinnerIcon className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      "Process Allocation"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={onCancel || onBack}
                    className="inline-flex items-center bg-gray-300 text-gray-700 py-2 px-4 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default AllotmentForm;
